import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';

/**
 * Accounts that asked to close. A superadmin starts processing once the
 * grace period has ended (or earlier, with the account's written request
 * recorded); the process-offboardings job then works through it.
 */

interface Closure {
  id: string;
  tenantName: string;
  status: string;
  reason: string | null;
  requestedAt: string;
  graceEndsAt: string;
  lastExportAt: string | null;
  completedAt: string | null;
  purgedAt: string | null;
  progress: Record<string, { done?: boolean } | string>;
  lastError: string | null;
}

export function ClosuresTab() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const [starting, setStarting] = useState<Closure | null>(null);
  const closures = useQuery({
    queryKey: ['platform-closures'],
    queryFn: () => api.get<Closure[]>('/api/platform/privacy/offboardings'),
  });
  if (closures.isLoading) return <Loading />;
  if (closures.error) return <ErrorState error={closures.error} onRetry={() => void closures.refetch()} />;
  const rows = closures.data ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Account closures</h3>
      </div>
      <div className="card-body small muted">
        An account that asks to close keeps export-only access for its grace period (SARTHI's policy). Processing erases its
        guests, staff and users and deletes technical data; tax invoices, payroll figures and the audit trail are
        kept. How long those kept records stay is a business and legal decision.
      </div>
      {rows.length === 0 ? (
        <Empty message="No account has asked to close" />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Account</th>
                <th>Asked</th>
                <th>Grace ends</th>
                <th>Exported</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const steps = Object.entries(row.progress).filter(([, value]) => typeof value === 'object');
                const done = steps.filter(([, value]) => (value as { done?: boolean }).done).length;
                return (
                  <tr key={row.id}>
                    <td>
                      <b>{row.tenantName}</b>
                      {row.reason ? <div className="xs muted">{row.reason}</div> : null}
                    </td>
                    <td>{dateTime(row.requestedAt)}</td>
                    <td>{dateTime(row.graceEndsAt)}</td>
                    <td>{row.lastExportAt ? dateTime(row.lastExportAt) : <span className="chip warn">Never</span>}</td>
                    <td>
                      {row.status}
                      {row.status === 'PROCESSING' ? <span className="xs muted"> ({done} steps done)</span> : null}
                      {row.status === 'COMPLETED' && row.completedAt ? (
                        <div className="xs muted">Closed {dateTime(row.completedAt)}; kept records await the final purge</div>
                      ) : null}
                      {row.purgedAt ? <div className="xs muted">Everything deleted {dateTime(row.purgedAt)}</div> : null}
                      {row.lastError ? <div className="xs muted">{row.lastError}</div> : null}
                    </td>
                    <td>
                      {isSuperadmin && ['GRACE_PERIOD', 'REQUESTED', 'FAILED'].includes(row.status) ? (
                        <button className="btn sm danger" onClick={() => setStarting(row)}>
                          Process…
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {starting ? <StartProcessing closure={starting} onClose={() => setStarting(null)} /> : null}
    </div>
  );
}

function StartProcessing({ closure, onClose }: { closure: Closure; onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const early = new Date(closure.graceEndsAt) > new Date();
  const [earlyReason, setEarlyReason] = useState('');
  const start = useMutation({
    mutationFn: () =>
      api.post(`/api/platform/privacy/offboardings/${closure.id}/process`, early ? { earlyReason } : {}),
    onSuccess: () => {
      push('Processing started', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-closures'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title={`Process the closure of ${closure.tenantName}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn danger"
            disabled={start.isPending || (early && earlyReason.trim().length < 10)}
            onClick={() => start.mutate()}
          >
            Start processing
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <p className="small">
          This cannot be undone. The account's personal data is erased - except what records kept under law contain - and its sign-ins are closed.
          {closure.lastExportAt ? '' : ' The account has never downloaded its data.'}
        </p>
        {early ? (
          <Field label="Why before the grace period ends?" hint="Record the account's written request, with its reference">
            <textarea className="input" rows={3} value={earlyReason} onChange={(e) => setEarlyReason(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Modal>
  );
}
